import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils.js";

// A person's photo, or their initials on the brand gradient when there is no
// photo (or it fails to load, e.g. an expired presigned URL). Photos are stored
// at 512x512 (see lib/profilePhoto.ts), so every size here stays sharp on retina.
const avatarVariants = cva(
  "relative inline-flex shrink-0 select-none items-center justify-center overflow-hidden bg-linear-135 from-brand-600 to-brand-500 font-bold text-white",
  {
    variants: {
      size: {
        xs: "size-6 text-[10px]",
        sm: "size-8 text-[11.5px]",
        md: "size-[34px] text-[13px]",
        lg: "size-16 text-[20px]",
        xl: "size-40 text-[44px]",
      },
      shape: {
        circle: "rounded-full",
        rounded: "rounded-[10px]",
      },
    },
    defaultVariants: { size: "sm", shape: "circle" },
  }
);

export const avatarDefaults = { size: "sm", shape: "circle" } as const;

export function initialsOf(name: string): string {
  const parts = name.trim().split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]![0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]![0] ?? "") : (parts[0]![1] ?? "");
  return (first + last).toUpperCase();
}

export interface AvatarProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, "children">,
    VariantProps<typeof avatarVariants> {
  /** Photo URL; null/undefined shows initials. */
  src?: string | null;
  /** Full name: the image's alt text and the source of the initials. */
  name: string;
}

export const Avatar = React.forwardRef<HTMLSpanElement, AvatarProps>(
  ({ className, src, name, size, shape, ...props }, ref) => {
    const [failed, setFailed] = React.useState(false);
    React.useEffect(() => setFailed(false), [src]);
    const showImg = !!src && !failed;
    return (
      <span ref={ref} className={cn(avatarVariants({ size, shape }), className)} {...props}>
        {showImg ? (
          <img
            src={src}
            alt={name}
            draggable={false}
            onError={() => setFailed(true)}
            className="size-full object-cover"
          />
        ) : (
          <span aria-label={name} role="img">{initialsOf(name)}</span>
        )}
      </span>
    );
  }
);
Avatar.displayName = "Avatar";

export { avatarVariants };
